import React, { ReactNode, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import Spacer from 'features/shared/Spacer';
import { Row } from 'features/shared/ui/Page';
import SelectWithSearching from 'features/shared/ui/SelectWithSearching';
import useMunicipalities from 'hooks/useMunicipalities';
import useMunicipalitySchools from 'hooks/useMunicipalitySchools';
import pluralize from 'utils/pluralize';

const Text = styled.div`
  margin-top: 4px;
  font-size: 16px;
  line-height: 22px;
  color: rgba(9, 21, 38, 0.6);
`;

const SelectText = styled(Text)`
  width: 385px;
`;
type Option = { value: number; label: string };
type Props = {
  value: Option | null;
  setValue: (value: Option | null) => void;
};
type SelectorProps = Props & {
  options: Option[];
  onOptionText: string;
  selectText: ReactNode;
  placeholder: string;
  qaLocator?: string;
};

const Component = (props: SelectorProps) => {
  return (
    <Row>
      <div style={{ width: '414px' }}>
        <SelectWithSearching
          value={props.value}
          onChange={(option) => props.setValue(option)}
          options={props.options}
          noOptionsText={props.onOptionText}
          placeholder={props.placeholder}
          defaultValue={props.value || undefined}
          data-qa-id={props.qaLocator}
        />
      </div>
      <Spacer size={24} inline />
      <SelectText>{props.selectText}</SelectText>
    </Row>
  );
};

export const SchoolSelector = (props: Props & { municipalityName?: string }) => {
  const { schools, loaded } = useMunicipalitySchools();
  const schoolsOptions = useMemo(
    () => [
      {
        value: -1,
        label: `${props.municipalityName} (${schools.length} ${pluralize(schools.length, 'школа', 'школы', 'школ')})`,
      },
      ...schools.map((el) => ({ value: el.id, label: el.name })),
    ],
    [schools],
  );

  useEffect(() => {
    if (loaded && !props.value && schoolsOptions.length > 0) {
      props.setValue(schoolsOptions[0]);
    }
  }, [schoolsOptions, loaded]);

  return (
    <Component
      value={props.value}
      setValue={props.setValue}
      options={schoolsOptions}
      onOptionText="Школа не найдена"
      selectText={
        <>
          Выберите школу для просмотра
          <br /> ее результатов
        </>
      }
      placeholder="Начните писать номер или название школы"
      qaLocator="schools_dropdown"
    />
  );
};

export const MunicipalitySelector = (props: Props & { regionName?: string }) => {
  const { municipalities, loaded } = useMunicipalities();

  const municipalitiesOptions = useMemo(
    () => [
      {
        value: -1,
        label: `${props.regionName} (${municipalities.length} ${pluralize(
          municipalities.length,
          'муниципалитет',
          'муниципалитета',
          'муниципалитетов',
        )})`,
      },
      ...municipalities.map((el) => ({ value: el.id, label: el.name })),
    ],
    [municipalities],
  );

  useEffect(() => {
    if (loaded && !props.value && municipalitiesOptions.length > 0) {
      props.setValue(municipalitiesOptions[0]);
    }
  }, [municipalitiesOptions, loaded]);

  return (
    <Component
      value={props.value}
      setValue={props.setValue}
      options={municipalitiesOptions}
      onOptionText="Муниципалитет не найдена"
      selectText="Выберите муниципалитет, если хотите посмотреть его статистику отдельно"
      placeholder="Начните писать название муниципалита"
      qaLocator="municipality_dropdown"
    />
  );
};
